import React, { useMemo } from 'react';
import EmptyContent from 'components/EmptyContent';
import Preloader from 'components/Preloader';
import RangeSlider from 'components/RangeSlider';
import { useYupValidationResolver } from 'hooks/useYupValidationResolver';
import { useFormatting } from 'locale';
import RiskScale from 'pages/ClientOverview/pages/Portfolios/components/RiskScale/RiskScale';
import { useForm } from 'react-hook-form';
import { PAYMENT_PERIODS } from 'pages/utils';
import {
    FormGroup, ProjectionChart, RadioButtonGroup, Select, TextInput, Title,
} from 'ui-library';
import { number, object, string } from 'yup';
import { ConsultationBox } from './components/ConsultationBox';
import { useGoalAndPerformance } from './hooks/useGoalAndPortfolio';
import './GoalAndPortfolioSetup.css';

const GoalAndPortfolioSetup = () => {
    const {
        t,
        riskCategories,
        modelPortfolio,
        projectionOptions,
        currency,
        productName,
        productSettings,
        changeTransferValue,
        changeYearlyPayment,
        transferValue,
        yearlyPayment,
        chartData,
        returnRates,
        minMaxTransferValue,
        minMaxYearlyPayment,
        isFullTransfer,
        radioFullTransferOptions,
        changeFullTransfer,
        showFullTransferQuestion,
        isLoading,
        validation,
        onFieldChange,
        prevPensionScheme,
        getSelectStrategy,
        isThreeB,
        periodicPayment,
        oneTimeInvestment,
        paymentPeriod,
        onPeriodicPaymentChange,
        onOneTimePaymentChange,
        onPaymentPeriodChange,
        rubrik,
        setRubrik,
        projectionYears,
        onProjectionYearsChange,
        getInvestmentForNextSixMonths,
        isProofOfAssetsRequired,
        language,
        isPaymentPeriodVisible,
        currentInvestmentDescriptionSelector,
        minPeriodicPaymentFor3B,
        minOneTimeInvestmentFor3B,
        isAzp, isZic, isZifd,
    } = useGoalAndPerformance({});

    const { getFormattedCurrency, getAmountWithDecimals, getFormattedDate } = useFormatting();

    const nextSixMonthsInvestment = getInvestmentForNextSixMonths();

    const schema = useMemo(() => {
        if (isZifd || !isZic) {
            return object().shape({
                periodicPaymentFrequency: number().typeError(t('validation.mandatoryField')).required(t('validation.mandatoryField')),
                rubrik: string(),
            });
        }

        return object().shape({});
    }, [t]);

    const resolver = useYupValidationResolver(schema);
    const paymentPeriods = useMemo(() => Object.values(PAYMENT_PERIODS).map(i => ({ label: t(i.label), value: i.id })), [t]);

    const {
        formState: { errors },
    } = useForm({ resolver });

    const RubrikInput = useMemo(() => (
        <TextInput
            value={rubrik}
            onChange={(e) => setRubrik(e.target.value)}
            label={t('3b.performance.rubrik')}
            error={validation?.rubrik}
            maxLength={30}
        />
    ), [rubrik, validation?.rubrik, language]);

    if (isAzp) {
        return (
            <div className="new-portfolio goal-product-setup newStyle">
                <Preloader isLoading={isLoading}>
                    <div className="azp-container">
                        <div className="azp-data">
                            <div className="azp-detail-data">
                                <span>{t('3b.withdrawalPlan.investmentAmount')}</span>
                                <span>
                                    {getAmountWithDecimals(currentInvestmentDescriptionSelector?.withdrawal?.investmentAmount)}
                                </span>
                            </div>
                            <div className="azp-detail-data">
                                <span>{t('3b.withdrawalPlan.withdrawalsStart')}</span>
                                <span>{getFormattedDate(currentInvestmentDescriptionSelector?.withdrawal?.startDate)}</span>
                            </div>
                            {RubrikInput}
                        </div>
                        <div className="azp-graph">
                            <div className="fi-verticals">
                                <Title type={2}>
                                    {t('3b.goalAndPortfolioSetup.estimatedPerformance')}
                                </Title>
                                <EmptyContent
                                    data={chartData}
                                    text={t('advisoryDashboard.noData')}
                                >
                                    <ProjectionChart
                                        data={chartData}
                                        yTitle={currency}
                                        {...projectionOptions}
                                        yearsText={t('charts.years')}
                                        showLegend
                                        returnRates={returnRates}
                                    />
                                </EmptyContent>
                            </div>
                        </div>
                    </div>
                </Preloader>
            </div>
        );
    }

    return (
        <div className="new-portfolio goal-product-setup newStyle">
            <Preloader isLoading={isLoading}>
                <div className="fi-verticals">
                    <div className="row">
                        <div className="col-lg-8">
                            <div className="fi-verticals">
                                <div className="form">
                                    {isZic || (
                                        <>
                                            <Title type={2}>{t('portfolio.createNewPortfolio.productConfiguration')}</Title>
                                            <FormGroup>
                                                <h3 className="Form-control-label">{t('portfolio.createNewPortfolio.chosenProduct')}</h3>
                                                <p className="button buttonRadioElement buttonRadioElementNotSelected button-small button-primary">{modelPortfolio ? modelPortfolio.Name : 'Not available'}</p>
                                            </FormGroup>
                                        </>
                                    )}
                                    <FormGroup>
                                        <h3 className="Form-control-label">{t('portfolio.createNewPortfolio.risk')}</h3>
                                        <RiskScale scales={riskCategories} activeIndex={getSelectStrategy()} />
                                    </FormGroup>
                                    {isThreeB || (
                                        <>
                                            {(productSettings?.showYearlyPayments) && (
                                                <RangeSlider
                                                    data-cy="yearlyPayment"
                                                    label={t('portfolio.createNewPortfolio.yearlyPayment')}
                                                    labelInfobox
                                                    error={validation?.yearlyPayment}
                                                    value={yearlyPayment}
                                                    onChange={changeYearlyPayment}
                                                    {...minMaxYearlyPayment}
                                                    withInput
                                                    showMinMax
                                                    disableExceedingMaxValue
                                                />
                                            )}
                                            <RangeSlider
                                                data-cy="transfer"
                                                label={t('portfolio.createNewPortfolio.transfer', { productName })}
                                                labelInfobox
                                                error={validation?.transferValue}
                                                value={transferValue}
                                                onChange={changeTransferValue}
                                                {...minMaxTransferValue}
                                                withInput
                                                showMinMax
                                                disableExceedingMaxValue
                                            />
                                        </>
                                    )}
                                </div>
                                {(showFullTransferQuestion && !isThreeB)
                                    && (
                                        <fieldset className="form-group full-transfer">
                                            <legend
                                                className="inputLabel"
                                            >
                                                {t('portfolio.createNewPortfolio.transferQuestion.legend')}
                                            </legend>
                                            <RadioButtonGroup
                                                value={isFullTransfer}
                                                options={radioFullTransferOptions}
                                                onChange={changeFullTransfer}
                                            />
                                        </fieldset>
                                    )}
                                {transferValue > 0 && !isThreeB ? (
                                    <div className="form">
                                        <Title className="title-pension-scheme" type={2}>{t('portfolio.createNewPortfolio.previousPensionScheme')}</Title>
                                        <TextInput
                                            onChange={(e) => {
                                                onFieldChange({ nameOfPensionScheme: e?.target?.value });
                                            }}
                                            value={prevPensionScheme?.nameOfPensionScheme || ''}
                                            error={validation?.nameOfPensionScheme}
                                            placeholder=""
                                            required
                                            label={t('portfolio.createNewPortfolio.nameOfPensionScheme')}
                                        />
                                        <TextInput
                                            onChange={(e) => {
                                                onFieldChange({ addressSupplement: e?.target?.value });
                                            }}
                                            value={prevPensionScheme?.addressSupplement || ''}
                                            placeholder=""
                                            label={t('portfolio.createNewPortfolio.addressSupplement')}
                                        />
                                        <TextInput
                                            onChange={(e) => {
                                                onFieldChange({ streetAndNumber: e?.target?.value });
                                            }}
                                            value={prevPensionScheme?.streetAndNumber || ''}
                                            placeholder=""
                                            label={t('portfolio.createNewPortfolio.streetAndNumber')}
                                        />
                                        <TextInput
                                            onChange={(e) => {
                                                onFieldChange({ postcodeAndCity: e?.target?.value });
                                            }}
                                            value={prevPensionScheme?.postcodeAndCity || ''}
                                            placeholder=""
                                            label={t('portfolio.createNewPortfolio.postcodeAndCity')}
                                        />
                                        <TextInput
                                            type="text"
                                            onChange={(e) => {
                                                onFieldChange({ policeNumber: e?.target?.value });
                                            }}
                                            value={prevPensionScheme?.policeNumber || ''}
                                            error={validation?.policeNumber}
                                            placeholder=""
                                            label={t('portfolio.createNewPortfolio.policeNumber')}
                                        />
                                        <TextInput
                                            onChange={(e) => {
                                                onFieldChange({ bankAccountNumber: e?.target?.value });
                                            }}
                                            type="text"
                                            error={validation?.bankAccountNumber}
                                            value={prevPensionScheme?.bankAccountNumber || ''}
                                            placeholder=""
                                            label={t('portfolio.createNewPortfolio.bankAccountNumber')}
                                        />
                                    </div>
                                ) : ''}
                                {isThreeB && (
                                    <div>
                                        <RangeSlider
                                            data-cy="oneTimeInvestment"
                                            label={t('portfolio.createNewPortfolio.oneTimeInvestment')}
                                            labelInfobox
                                            error={validation?.oneTimeInvestment}
                                            value={oneTimeInvestment}
                                            onChange={onOneTimePaymentChange}
                                            min={!isZic && (periodicPayment >= minPeriodicPaymentFor3B) ? minPeriodicPaymentFor3B : minOneTimeInvestmentFor3B}
                                            max={productSettings?.maxOneTimeInvestmentSlider}
                                            withInput
                                            showMinMax
                                        />

                                        {isZic || (
                                            <RangeSlider
                                                data-cy="periodicPayment"
                                                label={t('portfolio.createNewPortfolio.periodicPayment')}
                                                labelInfobox
                                                error={validation?.periodicPayment}
                                                value={periodicPayment}
                                                onChange={onPeriodicPaymentChange}
                                                min={minPeriodicPaymentFor3B}
                                                max={productSettings?.maxPeriodicPayment}
                                                withInput
                                                showMinMax
                                                enableZero
                                                disableExceedingMaxValue
                                            />
                                        )}
                                        {!isZic && isPaymentPeriodVisible && (
                                            <Select
                                                value={paymentPeriod}
                                                placeholder={t('3b.kyc.pleaseSelect')}
                                                label={t('3b.performance.periodicPaymentFrequency')}
                                                options={paymentPeriods}
                                                onChange={onPaymentPeriodChange}
                                                error={errors?.periodicPaymentFrequency?.message}
                                                required
                                            />
                                        )}
                                        <div className="planned-investment">
                                            {isZic || (
                                                <span>
                                                    {`${t('3b.performance.plannedInvestmentLabel')} ${getFormattedCurrency(nextSixMonthsInvestment, {
                                                        minimumFractionDigits: 0,
                                                        maximumFractionDigits: 0,
                                                        currency: 'CHF',
                                                    })}`}
                                                </span>
                                            )}
                                        </div>
                                        {RubrikInput}
                                    </div>
                                )}
                            </div>
                        </div>
                        {isZic || (
                            <div className="col-lg-12 projection-section">
                                {
                                    chartData.length ? (
                                        <>
                                            <Title type={2}>{t('portfolio.createNewPortfolio.calculationExample')}</Title>
                                            {isThreeB && (
                                                <div className="chart-subtitle-box">
                                                    <div>
                                                        <Title type={3} className="chart-subtitle">{t('portfolio.createNewPortfolio.previewHorizon')}</Title>
                                                        {!isZic && <p className="chart-subtitle-info">{t('portfolio.createNewPortfolio.previewInfo')}</p>}
                                                    </div>
                                                    <div className="years-selector">
                                                        {!isZic ? (
                                                            <RangeSlider
                                                                data-cy="previewHorizon"
                                                                unit="years"
                                                                value={projectionYears}
                                                                onChange={onProjectionYearsChange}
                                                                min={1}
                                                                max={80}
                                                                withInput
                                                                disableExceedingMaxValue
                                                            />
                                                        ) : (
                                                            <p className="projection-years">
                                                                {`${projectionYears} ${t('charts.years')}`}
                                                            </p>
                                                        )}
                                                    </div>
                                                </div>
                                            )}
                                            <ProjectionChart
                                                data={chartData}
                                                {...projectionOptions}
                                                yTitle={currency}
                                                yearsText={t('charts.years')}
                                                returnRates={returnRates}
                                                showLegend
                                            />
                                        </>
                                    ) : ''
                                }
                            </div>
                        )}
                    </div>
                </div>
                {isProofOfAssetsRequired && <ConsultationBox message="3b.performance.proofOfAssetsWarning" /> }
            </Preloader>
        </div>
    );
};

export default GoalAndPortfolioSetup;
